import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  OutlinedInput,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Select
} from '@mui/material';
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Scrollbar from '../../../components/Scrollbar';
import Iconify from '../../../components/Iconify';
import SearchNotFound from '../../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../../sections/@dashboard/user';
import JSON_CONST from '../../../components/CONSTVALUE.json';
import Loader from '../../Loader/Loader';

const TABLE_HEAD = [
  { id: 'id', label: '*Application No', alignRight: false },
  { id: 'bank', label: '*Bank Name', alignRight: false },
  { id: 'branch', label: '*Branch Name', alignRight: false },
  { id: 'customerBorrower', label: '*Name', alignRight: false },
  { id: 'phoneNo', label: '*Phone No', alignRight: false },
  { id: 'statusValue', label: '*Status', alignRight: false }
];

export default function PrepareReports() {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('bank');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [USERLIST, setUSERLIST] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Bank filter
  const [BankSearch, setBankSearch] = useState('');

  // Dropdown data
  const [fromDataAutoFill, setFromDataAutoFill] = useState({
    bankList: [],
    branchList: [],
    userList: [],
    handledByList: [],
  });

  // Fetch banks, branches, users, handledBy
  const arrageList = (response) => {
    const list = [];
    response.data.forEach((row) => {
      if (row.status === 'true') {
        list.push(row);
      }
    });
    return list;
  };

  const loadDropdowns = async () => {
    let bankList = [];
    let branchList = [];
    let userList = [];
    let handledByList = [];

    await axios.get(`${JSON_CONST.DB_URL}master/bank/list`)
      .then((response) => { bankList = arrageList(response); });

    await axios.get(`${JSON_CONST.DB_URL}master/branch/list`)
      .then((response) => { branchList = arrageList(response); });

    await axios.get(`${JSON_CONST.DB_URL}auth/userList`)
      .then((response) => { userList = arrageList(response); });

    await axios.get(`${JSON_CONST.DB_URL}master/handledBy/list`)
      .then((response) => { handledByList = arrageList(response); });

    setFromDataAutoFill({ bankList, branchList, userList, handledByList });
  };

  // Fetch reports data
  const fetchData = async (bankId = '') => {
    try {
      let url = `${JSON_CONST.DB_URL}option/list`;
      if (bankId) {
        url += `?bank=${bankId}`;
      }
      const response = await axios.get(url);
      setUSERLIST(response.data);
      setFilteredData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  // On load
  useEffect(() => {
    loadDropdowns();
    fetchData(); // last 10 by default
  }, []);

  // On bank change
  const banksearchList = (e) => {
    const query = e.target.value;
    setBankSearch(query);
    fetchData(query); // fetch bank wise data
  };

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const handleSearch = (query) => {
    const filtered = USERLIST.filter((item) => {
      const searchString = `${item.reciptDate} ${item.id} ${item.fileNo} ${item.bankName?.name} ${item.branchName?.name} ${item.email} ${item.phoneNo} ${item.customerBorrower} ${item.statusValue}`.toLowerCase();
      return searchString.includes(query.toLowerCase());
    });
    setFilteredData(filtered);
  };

  const handleInputChange = e => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query === '') {
      setFilteredData(USERLIST);
    } else {
      handleSearch(query);
    }
  };
  // Sorting
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
    width: 240,
    transition: theme.transitions.create(['box-shadow', 'width'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
    '& fieldset': {
      borderWidth: `1px !important`,
      borderColor: `${theme.palette.grey[500_32]} !important`,
    },
  }));

  const isUserNotFound = filteredData.length === 0;

  if (isLoading) return <Loader />;

  return (
    <Container maxWidth="">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom>
          Vetting Reports
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/app/option/prepareReports/newEntry/0')}
          startIcon={<Iconify icon="eva:plus-fill" />}
        >
          New Entry
        </Button>
      </Stack>

      <Card sx={{ width: '100%' }}>
        <Grid container alignItems="center" paddingLeft={10} paddingBottom={10} paddingRight={10} paddingTop={5} spacing={3}>
                      <Grid item xs={12} sm={12} md={9} lg={9}>
                        <SearchStyle
                          style={{marginTop: '20px', marginLeft: '20px'}}
                          value={searchQuery}
                          onChange={handleInputChange}
                          autoFocus
                          // eslint-disable-next-line no-template-curly-in-string
                          placeholder={`Search from " *yellow fields "`}
                          startAdornment={
                            <InputAdornment position="start">
                              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                            </InputAdornment>
                          }
                        />
                      </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="Bank-select-label">Bank</InputLabel>
              <Select
                labelId="Bank-select-label"
                id="Bank-select"
                value={BankSearch}
                label="bank"
                name="bank"
                fullWidth
                onChange={banksearchList}
              >
                <MenuItem value=''>All</MenuItem>
                {fromDataAutoFill.bankList.map((option) => (
                  <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 100 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={USERLIST.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell align="left">{row.id}</TableCell>
                      <TableCell align="left">
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="subtitle2" noWrap>
                            {row.bankName?.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">{row.branchName?.name}</TableCell>
                      <TableCell align="left">{row.customerBorrower}</TableCell>
                      <TableCell align="left">{row.phoneNo}</TableCell>
                      <TableCell align="left">
                        <Label variant="ghost" color="secondary">
                          {row.statusValue || 'NA'}
                        </Label>
                      </TableCell>
                      <TableCell align="right">
                        <UserMoreMenu
                          urlTo={'/app/option/prepareReports/newEntry/'}
                          data={row}
                          dataID={row.id}
                          deleteURL={'option/delete/'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                {isUserNotFound && (
                  <TableRow>
                    <TableCell align="center" colSpan={6}>
                      <SearchNotFound searchQuery={searchQuery} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={USERLIST.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Container>
  );
}
